<template>
	<div class="width-full py-4 px-6">
		<div v-if="error" class="d-flex justify-end">
			<p class="text-error-800 pb-2">{{ error }}</p>
		</div>
		<div
			class="width-full d-flex justify-end gap-2"
			:class="[verticalActions ? 'flex-column align-inherit' : 'align-center']"
		>
			<PButton
				v-if="cancelAction"
				:type="cancel.buttonType"
				:color="cancel.color"
				:class="invert ?? 'order-2'"
				:disabled="disableCancel"
				@click="$emit('cancel')"
			>
				{{ cancel.text }}
			</PButton>
			<PButton
				v-if="acceptAction"
				:type="accept.buttonType"
				:color="accept.color"
				:class="[invert ?? 'order-1', acceptClasses]"
				:disabled="disableAccept"
				@click="$emit('accept')"
			>
				{{ accept.text }}
			</PButton>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed } from 'vue'
	import { useI18n } from 'vue-i18n'
	import { DialogActionConfig } from './dialogTypes'
	import PButton from '../PButton.vue'

	const { t } = useI18n()

	interface Props {
		acceptAction?: DialogActionConfig | boolean
		cancelAction?: DialogActionConfig | boolean
		disableAccept?: boolean
		disableCancel?: boolean
		invert?: boolean
		error?: string
		acceptClasses?: string
		verticalActions?: boolean
	}

	const props = withDefaults(defineProps<Props>(), {
		acceptAction: false,
		cancelAction: false,
		disableAccept: false,
		disableCancel: false,
		invert: false,
		error: '',
		acceptClasses: '',
		verticalActions: false,
	})

	const defaultAccept: DialogActionConfig = {
		text: t('common_accept'),
		color: 'primary',
		buttonType: 'filled',
	}

	const defaultCancel: DialogActionConfig = {
		text: t('Common_cancel'),
		color: 'neutral',
		buttonType: 'outlined',
	}

	const accept = computed<DialogActionConfig>(() => {
		if (typeof props.acceptAction === 'boolean') return defaultAccept
		return {
			text: props.acceptAction?.text || defaultAccept.text,
			color: props.acceptAction?.color || defaultAccept.color,
			buttonType: props.acceptAction?.buttonType || defaultAccept.buttonType,
		}
	})

	const cancel = computed<DialogActionConfig>(() => {
		if (typeof props.cancelAction === 'boolean') return defaultCancel
		return {
			text: props.cancelAction?.text || defaultCancel.text,
			color: props.cancelAction?.color || defaultCancel.color,
			buttonType: props.cancelAction?.buttonType || defaultCancel.buttonType,
		}
	})

	defineEmits(['cancel', 'accept'])
</script>

<style scoped>
	.order-1 {
		order: 1;
	}
	.order-2 {
		order: 2;
	}
</style>
